/**
 * メインAppコンポーネント
 */

import { useEffect, useState, useCallback } from 'react';
import { useGameStore } from '@stores/gameStore';
import { useUIStore } from '@stores/uiStore';
import { socketClient } from '@services/websocket/socketClient';
import { GameBoard } from '@components/game/GameBoard';
import { Loading } from '@components/common/Loading';

/**
 * GameBoardの基準サイズ（px）
 */
const BASE_WIDTH = 1020;
const BASE_HEIGHT = 1200;

/**
 * ウィンドウサイズに応じたスケール値を計算するカスタムフック
 */
const useResponsiveScale = () => {
  const calculateScale = useCallback(() => {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // 幅と高さそれぞれに対するスケール比率を計算
    const scaleX = windowWidth / BASE_WIDTH;
    const scaleY = windowHeight / BASE_HEIGHT;

    // 小さい方のスケールを採用（画面からはみ出さないように）
    // 最小0.3、最大1.5に制限
    return Math.max(0.3, Math.min(scaleX, scaleY, 1.5));
  }, []);

  const [scale, setScale] = useState(calculateScale);

  useEffect(() => {
    const handleResize = () => {
      setScale(calculateScale());
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [calculateScale]);

  return scale;
};

function App() {
  const { updateGameState } = useGameStore();
  const { isLoading, loadingMessage, errorMessage, setLoading, setError } = useUIStore();
  const scale = useResponsiveScale();

  useEffect(() => {
    // WebSocketイベントリスナー設定
    socketClient.setListeners({
      onGameJoined: () => {
        setLoading(false);
      },
      onActionExecuted: (data) => {
        updateGameState(data.game_state);
      },
      onAIThinking: () => {
        setLoading(true, 'AI思考中...');
      },
      onAIAction: (data) => {
        updateGameState(data.game_state);
        setLoading(false);
      },
      onRoundStarted: (data) => {
        console.log(`🔄 ラウンド ${data.round} 開始`);
        updateGameState(data.game_state);
        setLoading(false);
      },
      onGameStateUpdate: (data) => {
        console.log('🔄 ゲーム状態更新を受信');
        updateGameState(data.game_state);
        setLoading(false);
      },
      onError: (data) => {
        setError(data.message);
      },
    });
  }, []);

  return (
    <div
      className="bg-gray-900 flex items-center justify-center"
      style={{
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      {/* スケーリングコンテナ */}
      <div
        style={{
          width: `${BASE_WIDTH}px`,
          height: `${BASE_HEIGHT}px`,
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
        }}
      >
        <GameBoard />
      </div>

      {errorMessage && (
        <div className="fixed bottom-4 right-4 bg-red-500 text-white p-4 rounded shadow-lg z-50">
          {errorMessage}
        </div>
      )}

      {isLoading && <Loading message={loadingMessage} />}
    </div>
  );
}

export default App;
